// The Terminal console's status line + rail dot data layer (#4395, epic
// #4391). Reads the one read-only health snapshot `GET /v1/terminal/status` —
// composed server-side from the SAME functions Fleet's utilization card and
// the overview strip use (D-C), never a second copy — so this file is a
// client-side MIRROR of `apps/api/src/routes/terminal.ts`'s
// `TerminalStatusResponse`, exactly the pattern `lib/fleetUtilization.ts`
// already uses for its own unpublished route: apps never import apps, so the
// server type cannot be imported directly.
//
// TWO CONSUMERS, TWO POLL RATES (D-O): the rail dot at 30s, the page's status
// line at 5s. Each call site passes its own `pollMs`, folded into the query
// key so the two never fight over one cache entry's `refetchInterval`.

import type { TaskClass } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** D-P's three-way verdict — the rail dot's tone and the status cell's word. */
export type TerminalHealthStatus = 'ok' | 'degraded' | 'down';

export type TerminalStatusWorkType = { readonly class: TaskClass; readonly running: number };

/** The whole `GET /v1/terminal/status` body. */
export type TerminalStatus = {
  readonly status: TerminalHealthStatus;
  readonly workers: {
    readonly online: number;
    readonly total: number;
    readonly by_class: readonly TerminalStatusWorkType[];
  };
  readonly keys: {
    readonly usable: number;
    readonly cooling: number;
    readonly subs_exhausted: boolean;
  };
  readonly queue: {
    readonly depth: number;
    readonly head_class: TaskClass | null;
    readonly oldest_pending_age_ms: number | null;
  };
  readonly retries_last_hour: number;
  readonly fail_rate_last_hour: { readonly ended: number; readonly failed: number };
  readonly prs_24h: { readonly merged: number; readonly opened: number };
};

export const TERMINAL_STATUS_QUERY_KEY = 'terminal_status';

/** The rail dot's own poll cadence (D-O: "the rail polls … at 30s"). */
export const TERMINAL_RAIL_DOT_POLL_MS = 30_000;
/** The page status line's own poll cadence (D-O: "… and the page at 5s"). */
export const TERMINAL_STATUS_LINE_POLL_MS = 5_000;

/**
 * The DEGRADED threshold D-P declares for the oldest pending task — reused
 * here to tone the PENDING cell rather than inventing a second number that
 * could drift from the server's own verdict.
 */
export const PENDING_WARN_MS = 30 * 60_000;

/**
 * Compact duration for a dense status cell: "45s" / "2m" / "3h" / "1d" — the
 * same ASCII control-plane-cell ladder `lib/time.ts`'s `since()` uses for an
 * ISO age, over a raw millisecond DURATION instead (`oldest_pending_age_ms`
 * has no timestamp to diff against `Date.now()`, so it cannot go through
 * `since()` itself). NaN- and negative-safe: an unparseable or clock-skewed
 * value renders as `0s` rather than `NaNs` or a negative age.
 */
export function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '0s';
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

/**
 * The account's Terminal status snapshot, polled at `pollMs`. Idle with no
 * session. Keyed by the active org (an org switch re-reads under the new
 * tenant) AND by `pollMs` (the rail's 30s read and the page's 5s read are
 * deliberately two cache entries, never one fighting over an interval).
 */
export function useTerminalStatus(
  token: string | null = SESSION_TOKEN,
  pollMs: number = TERMINAL_STATUS_LINE_POLL_MS,
) {
  return createQuery<TerminalStatus>(() => ({
    queryKey: [TERMINAL_STATUS_QUERY_KEY, pollMs, token, activeAccountId()],
    enabled: token !== null,
    refetchInterval: pollMs,
    queryFn: () => apiFetch<TerminalStatus>('/v1/terminal/status', token),
  }));
}
